import { supabase } from '@/integrations/supabase/client'

// Session timeout configuration (30 minutes)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const SESSION_WARNING_MS = 5 * 60 * 1000 // Warn 5 minutes before timeout

class SessionManager {
  private timeoutId: NodeJS.Timeout | null = null
  private warningTimeoutId: NodeJS.Timeout | null = null
  private onSessionWarning?: () => void
  private onSessionExpired?: () => void

  constructor() {
    this.setupActivityListeners()
  }

  // Set up listeners for user activity
  private setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    
    events.forEach(event => {
      document.addEventListener(event, this.resetTimer.bind(this), true)
    })
  }

  // Start session timeout
  startTimer() {
    this.clearTimers()
    
    // Set warning timer
    this.warningTimeoutId = setTimeout(() => {
      this.onSessionWarning?.()
    }, SESSION_TIMEOUT_MS - SESSION_WARNING_MS)
    
    // Set expiration timer
    this.timeoutId = setTimeout(async () => {
      await this.expireSession()
    }, SESSION_TIMEOUT_MS)
  }

  // Reset timers on user activity
  private resetTimer() {
    this.startTimer()
  }

  // Clear all timers
  private clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
      this.warningTimeoutId = null
    }
  }

  // Expire the session
  private async expireSession() {
    try {
      await this.cleanupAuthState()
      await supabase.auth.signOut({ scope: 'global' })
      this.onSessionExpired?.()
    } catch (error) {
      console.error('Error expiring session:', error)
      // Force cleanup even if signOut fails
      this.cleanupAuthState()
      window.location.href = '/'
    }
  }

  // Clean up authentication state
  private async cleanupAuthState() {
    // Clear all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key)
      }
    })

    // Clear from sessionStorage if in use
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          sessionStorage.removeItem(key)
        }
      })
    }
  }

  // Set callback handlers
  onWarning(callback: () => void) {
    this.onSessionWarning = callback
  }

  onExpired(callback: () => void) {
    this.onSessionExpired = callback
  }

  // Stop the session timer
  stopTimer() {
    this.clearTimers()
  }

  // Manual session extension
  extendSession() {
    this.startTimer()
  }

  // Get time remaining until session expires
  getTimeRemaining(): number {
    // This is approximate since we don't track the exact start time
    return SESSION_TIMEOUT_MS
  }
}

export const sessionManager = new SessionManager()
export { SESSION_TIMEOUT_MS, SESSION_WARNING_MS }